import {QueryClient, UseMutationOptions, UseQueryResult, useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import IWBrokerClient from '../client/IWBrokerClient';
import {invalidateGatewayUserScopedQueries} from './gatewayQueryUtils';
import {queryClient as defaultQueryClient} from './queryClient';

export type CurrentUser = Record<string, any>;

export const currentUserQueryKey = ['currentUser'] as const;

const installedCurrentUserListeners = new WeakSet<QueryClient>();

export function setCurrentUser(
    user: CurrentUser | null | undefined,
    queryClient: QueryClient = defaultQueryClient,
    options?: {invalidateUserScoped?: boolean}
) {
    const previousUser = queryClient.getQueryData<CurrentUser | null>(currentUserQueryKey);
    const nextUser = user ?? null;

    queryClient.setQueryData(currentUserQueryKey, nextUser);

    if (!options?.invalidateUserScoped && previousUser?.id === nextUser?.id) {
        return;
    }

    invalidateGatewayUserScopedQueries(queryClient);
}

export function installCurrentUserQueryListener(queryClient: QueryClient = defaultQueryClient) {
    if (installedCurrentUserListeners.has(queryClient)) {
        return;
    }

    IWBrokerClient.socket.on('user', (user: CurrentUser | null) => {
        console.info('(currentUserQuery) user', user);
        setCurrentUser(user, queryClient, {invalidateUserScoped: true});
    });

    installedCurrentUserListeners.add(queryClient);
}

export function useCurrentUserQuery(): UseQueryResult<CurrentUser | null, Error> {
    const queryClient = useQueryClient();

    return useQuery<CurrentUser | null>({
        queryKey: currentUserQueryKey,
        queryFn: async (): Promise<CurrentUser | null> => {
            return queryClient.getQueryData<CurrentUser | null>(currentUserQueryKey) ?? null;
        },
        staleTime: Infinity,
        gcTime: 1000 * 60 * 60 * 24,
        networkMode: 'always',
        retry: false
    });
}

export async function loginCurrentUserWithGoogle(
    idToken: string,
    queryClient: QueryClient = defaultQueryClient
): Promise<CurrentUser> {
    const user = await IWBrokerClient.request('google login', {idToken}) as CurrentUser;

    setCurrentUser(user, queryClient);

    return user;
}

export async function logoutCurrentUser(queryClient: QueryClient = defaultQueryClient): Promise<void> {
    await IWBrokerClient.request('logout', {});

    setCurrentUser(null, queryClient, {invalidateUserScoped: true});
}

export function useGoogleLoginMutation(
    options?: UseMutationOptions<CurrentUser, unknown, string>
) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (idToken: string) => loginCurrentUserWithGoogle(idToken, queryClient),
        ...options
    });
}

export function useLogoutMutation(options?: UseMutationOptions<void, unknown, void>) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: () => logoutCurrentUser(queryClient),
        ...options
    });
}
